import type { FC, ReactNode } from 'react'
import React, { createContext, useContext } from 'react'
import styles from './radio.module.css'

interface RadioGroupContextValue {
  name?: string
  value?: string | number
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const RadioGroupContext = createContext<RadioGroupContextValue>({})

export interface RadioGroupProps {
  name?: string
  value?: string | number
  defaultValue?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  children?: ReactNode
  row?: boolean
  className?: string
  style?: React.CSSProperties
  'aria-label'?: string
  'aria-labelledby'?: string
}

export const RadioGroup: FC<RadioGroupProps> = ({
  name,
  value: valueProp,
  defaultValue,
  onChange,
  children,
  row,
  className,
  style,
  ...rest
}) => {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue)
  const isControlled = valueProp !== undefined
  const value = isControlled ? valueProp : uncontrolledValue
  const classes = [styles.radioGroup, row ? styles.row : undefined, className]
    .filter(Boolean)
    .join(' ')

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) {
      setUncontrolledValue(event.target.value)
    }
    onChange?.(event, event.target.value)
  }

  return (
    <RadioGroupContext.Provider value={{ name, value, onChange: handleChange }}>
      <div role="radiogroup" className={classes} style={style} {...rest}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  )
}

export interface RadioProps {
  value?: string | number
  checked?: boolean
  disabled?: boolean
  name?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  size?: 'small' | 'medium'
  color?: 'primary' | 'secondary' | 'default'
  className?: string
  style?: React.CSSProperties
}

export const Radio: FC<RadioProps> = ({
  value,
  checked: checkedProp,
  disabled,
  name: nameProp,
  onChange: onChangeProp,
  size = 'medium',
  className,
  style,
}) => {
  const group = useContext(RadioGroupContext)
  const name = nameProp ?? group.name
  const checked = checkedProp ?? (group.value != null ? String(group.value) === String(value) : undefined)
  const onChange = onChangeProp ?? group.onChange

  const iconSize = size === 'small' ? 20 : 24
  const classes = [styles.radio, className].filter(Boolean).join(' ')

  return (
    <span className={classes} style={style}>
      <input
        type="radio"
        className={styles.input}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <svg
        className={styles.icon}
        focusable="false"
        aria-hidden="true"
        viewBox="0 0 24 24"
        style={{ width: iconSize, height: iconSize }}
      >
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
        {checked ? <circle cx="12" cy="12" r="5" fill="currentColor" /> : null}
      </svg>
    </span>
  )
}
